import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ReviewDisplay from './ReviewDisplay';
import RatingForm from './RatingForm';

const UserDashboard = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [userReviews, setUserReviews] = useState([]);
  const [userStats, setUserStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    helpfulVotes: 0,
    recentActivity: []
  });

  // Mock data for demonstration
  useEffect(() => {
    if (isAuthenticated && user) {
      // Simulate loading user reviews and stats
      const mockReviews = [
        {
          id: 1,
          locationName: 'Central Park',
          address: 'Manhattan, NY',
          overallSafety: 4,
          lightingQuality: 4,
          crowdLevels: 5,
          staffHelpfulness: 3,
          harassmentIncident: false,
          comments: 'Great place for a morning jog. Well-lit paths and plenty of people around.',
          timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          username: user.username,
          userId: user.id,
          helpfulVotes: 8,
          unhelpfulVotes: 1,
          photos: []
        },
        {
          id: 2,
          locationName: 'Downtown Metro Station',
          address: 'Downtown, NY',
          overallSafety: 2,
          lightingQuality: 2,
          crowdLevels: 4,
          staffHelpfulness: 2,
          harassmentIncident: true,
          harassmentSeverity: 'medium',
          comments: 'Poor lighting in the evening. Experienced some harassment near the entrance.',
          timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          username: user.username,
          userId: user.id,
          helpfulVotes: 12,
          unhelpfulVotes: 2,
          photos: []
        },
        {
          id: 3,
          locationName: 'Riverside Mall',
          address: 'Riverside, NY',
          overallSafety: 5,
          lightingQuality: 5,
          crowdLevels: 3,
          staffHelpfulness: 5,
          harassmentIncident: false,
          comments: 'Excellent security and staff. Very safe for families.',
          timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          username: user.username,
          userId: user.id,
          helpfulVotes: 15,
          unhelpfulVotes: 0,
          photos: []
        }
      ];

      setUserReviews(mockReviews);
      
      const stats = {
        totalReviews: mockReviews.length,
        averageRating: mockReviews.reduce((acc, review) => acc + review.overallSafety, 0) / mockReviews.length,
        helpfulVotes: mockReviews.reduce((acc, review) => acc + (review.helpfulVotes || 0), 0),
        recentActivity: [
          { type: 'review', action: 'posted', location: 'Central Park', date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000) },
          { type: 'vote', action: 'received helpful vote', location: 'Riverside Mall', date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000) },
          { type: 'review', action: 'posted', location: 'Downtown Metro Station', date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000) }
        ]
      };
      
      setUserStats(stats);
    }
  }, [isAuthenticated, user]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'review': return '📝';
      case 'vote': return '👍';
      case 'photo': return '📸';
      default: return '📋';
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return '#4CAF50';
    if (rating >= 3) return '#FF9800';
    return '#f44336';
  };

  if (!isOpen || !isAuthenticated) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '5px',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: '#2196F3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '24px',
              fontWeight: 'bold'
            }}>
              {user?.profile?.displayName ? user.profile.displayName.charAt(0).toUpperCase() : user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
                {user?.profile?.displayName || user?.username || 'User Dashboard'}
              </h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>
                Member since {formatDate(user?.createdAt || new Date())}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div style={{ padding: '24px', borderBottom: '1px solid #e0e0e0' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
            Your Safety Contributions
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px'
          }}>
            <div style={{
              backgroundColor: '#f9f9f9',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196F3' }}>
                {userStats.totalReviews}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Reviews Posted</div>
            </div>
            <div style={{
              backgroundColor: '#f9f9f9',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: getRatingColor(userStats.averageRating) }}>
                {userStats.averageRating.toFixed(1)}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Average Rating</div>
            </div>
            <div style={{
              backgroundColor: '#f9f9f9',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50' }}>
                {userStats.helpfulVotes}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Helpful Votes</div>
            </div>
            <div style={{
              backgroundColor: '#f9f9f9',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FF9800' }}>
                {user?.reputation || 0}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Reputation</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f9f9f9'
        }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'reviews', label: 'My Reviews' },
            { id: 'activity', label: 'Activity' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '12px 8px',
                border: 'none',
                backgroundColor: activeTab === tab.id ? '#fff' : 'transparent',
                color: activeTab === tab.id ? '#2196F3' : '#666',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '600' : '400',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '2px solid #2196F3' : 'none'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {activeTab === 'overview' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                Recent Activity
              </h3>
              <div style={{ marginBottom: '24px' }}>
                {userStats.recentActivity.map((activity, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    marginBottom: '8px'
                  }}>
                    <span style={{ fontSize: '20px' }}>{getActivityIcon(activity.type)}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', color: '#333' }}>
                        You {activity.action} for <strong>{activity.location}</strong>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {formatDate(activity.date)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                Your Recent Reviews
              </h3>
              {userReviews.slice(0, 2).map((review) => (
                <div key={review.id} style={{
                  backgroundColor: '#f9f9f9',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#333' }}>
                        {review.locationName}
                      </h4>
                      <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#666' }}>
                        {review.address}
                      </p>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      backgroundColor: getRatingColor(review.overallSafety),
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      ★ {review.overallSafety}/5
                    </div>
                  </div>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#555' }}>
                    {review.comments}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      {formatDate(review.timestamp)}
                    </span>
                    <span style={{ fontSize: '12px', color: '#4CAF50' }}>
                      {review.helpfulVotes} helpful votes
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                  All My Reviews ({userReviews.length})
                </h3>
              </div>
              
              {userReviews.length > 0 ? (
                userReviews.map((review) => (
                  <div key={review.id} style={{
                    backgroundColor: '#f9f9f9',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px'
                    }}>
                      <div>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#333' }}>
                          {review.locationName}
                        </h4>
                        <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#666' }}>
                          {review.address}
                        </p>
                        <p style={{ margin: '0', fontSize: '12px', color: '#666' }}>
                          Posted on {formatDate(review.timestamp)}
                        </p>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: getRatingColor(review.overallSafety),
                        color: 'white',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        ★ {review.overallSafety}/5
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: '8px',
                      marginBottom: '12px',
                      padding: '8px',
                      backgroundColor: '#fff',
                      borderRadius: '4px'
                    }}>
                      <div style={{ fontSize: '12px' }}>
                        <span style={{ color: '#666' }}>Lighting:</span> {review.lightingQuality}/5
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        <span style={{ color: '#666' }}>Crowd:</span> {review.crowdLevels}/5
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        <span style={{ color: '#666' }}>Staff:</span> {review.staffHelpfulness}/5
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        <span style={{ color: '#666' }}>Incidents:</span> {review.harassmentIncident ? 'Yes' : 'No'}
                      </div>
                    </div>
                    
                    {review.comments && (
                      <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#555', fontStyle: 'italic' }}>
                        "{review.comments}"
                      </p>
                    )}
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: '8px',
                      borderTop: '1px solid #e0e0e0'
                    }}>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Community feedback:
                      </div>
                      <div style={{ fontSize: '12px', color: '#4CAF50' }}>
                        👍 {review.helpfulVotes} helpful • 👎 {review.unhelpfulVotes} unhelpful
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#666'
                }}>
                  <p>You haven't posted any reviews yet.</p>
                  <p style={{ fontSize: '14px' }}>
                    Start reviewing locations to help keep the community safe!
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>
                Activity History
              </h3>
              <div>
                {userStats.recentActivity.map((activity, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#2196F3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px'
                    }}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                        You {activity.action} for <strong>{activity.location}</strong>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {formatDate(activity.date)} • {new Date(activity.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;